import React, { useState, useEffect, useRef, useImperativeHandle, useMemo } from 'react';
import { useBrgList } from '../../hooks/useBrgList';
import { useUserAssignedBrgs } from '../../hooks/useUserAssignedBrgs';
import { engagementMetricsApi } from '../../api/engagementMetrics';
import { useProjects, useCreateProject, useUpdateProject, useDeleteProject, useCreateTask, useDeleteTask, useUpdateTask, useCreateMessage, useProjectPillars } from '../../api/projects';
import { TaskAssigneesField } from './TaskAssigneesField';
import { MentionInput } from './MentionInput';
import { renderMessageWithTags, taggedUsersInText } from './messageTags';
import { ProjectDto, CreateProjectRequest, UpdateProjectRequest, CreateProjectTaskRequest, UpdateProjectTaskRequest, ProjectTaskDto, UserDropdownDto } from '../../types';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { FilterSelect } from '../../components/shared/FilterSelect';
import Toast, { useToast } from '../../components/shared/Toast';
import { ConfirmDialog } from '../../components/shared/ConfirmDialog';
import { DateInputDDMMYYYY } from '../../components/shared/DateInputDDMMYYYY';
import { TaskModal } from './TaskModal';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import { isAdmin, isLeadRole } from '../../auth/roleUtils';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';

/** Format an ISO date string (yyyy-mm-dd) for display without timezone shift */
function formatDateLocal(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
}

interface Filters {
  brgId?: number;
  status?: string;
  company?: string;
  priority?: string;
}



export const ProjectTrackingPage: React.FC = () => {
  const { data: brgs = [] } = useBrgList();
  const { data: userAssignedBrgs = [] } = useUserAssignedBrgs();
  const auth = useAuth();
  const { toasts, dismissToast, success, error } = useToast();
  const isUserAdmin = isAdmin(auth.roles);
  const isUserLead = isLeadRole(auth.roles);
  const canManageProjects = isUserAdmin || isUserLead;
  
  // For Leads: filter BRGs to only show assigned ones; For Admins: show all
  const availableBRGs = isUserLead && userAssignedBrgs.length > 0
    ? userAssignedBrgs
    : brgs;
  
  // Fetch companies from database
  const [companyOptions, setCompanyOptions] = useState<string[]>([]);
  useEffect(() => {
    engagementMetricsApi.companies().then(resp => {
      setCompanyOptions(resp.companies);
    }).catch(() => {});
  }, []);
  
  const [filters, setFilters] = useState<Filters>({});
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Ref to track pending project switch after save
  const pendingProjectIdRef = useRef<number | null>(null);
  
  // Ref to trigger save from ProjectDetailPanel
  const projectDetailSaveRef = useRef<{ save: () => Promise<void> } | null>(null);
  
  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    isDangerous?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Task modal state
  const [editingTask, setEditingTask] = useState<ProjectTaskDto | undefined>(undefined);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const { data: projects = [], isLoading } = useProjects(filters);
  const selectedProject = projects.find((p: ProjectDto) => p.projectId === selectedProjectId);


  // Auto-select first project on load or when selected project is no longer in list
  useEffect(() => {
    if (projects.length > 0 && !showNewProjectForm) {
      if (!selectedProjectId || !projects.find((p: ProjectDto) => p.projectId === selectedProjectId)) {
        setSelectedProjectId(projects[0].projectId);
      }
    }
  }, [projects, selectedProjectId, showNewProjectForm]);

  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject(selectedProjectId || 0);
  const deleteProjectMutation = useDeleteProject(selectedProjectId || 0);
  const createTaskMutation = useCreateTask(selectedProjectId || 0);
  const updateTaskMutation = useUpdateTask(selectedProjectId || 0);
  const deleteTaskMutation = useDeleteTask(selectedProjectId || 0);
  const createMessageMutation = useCreateMessage(selectedProjectId || 0);

  const handleFilterChange = (key: keyof Filters, value: any) => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        isOpen: true,
        title: 'Discard Changes?',
        message: 'You have unsaved changes. Do you want to discard them before changing filters?',
        confirmText: 'Discard',
        cancelText: 'Keep Editing',
        onConfirm: () => {
          setHasUnsavedChanges(false);
          setFilters((prev) => ({ ...prev, [key]: value }));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        },
      });
      return;
    }
    setFilters(prev => ({ ...prev, [key]: value }));
    setSelectedProjectId(null);
  };

  const handleClearFilters = () => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        isOpen: true,
        title: 'Discard Changes?',
        message: 'You have unsaved changes. Do you want to discard them?',
        confirmText: 'Discard',
        cancelText: 'Keep Editing',
        onConfirm: () => {
          setHasUnsavedChanges(false);
          setFilters({});
          setSelectedProjectId(null);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        },
      });
      return;
    }
    setFilters({});
    setSelectedProjectId(null);
  };

  const handleSelectProject = (projectId: number) => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        isOpen: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Do you want to save them before switching to another project?',
        confirmText: 'Save & Switch',
        cancelText: 'Keep Editing',
        onConfirm: async () => {
          // Store the target project ID in ref so it survives the save
          pendingProjectIdRef.current = projectId;
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          
          // Trigger save in ProjectDetailPanel
          try {
            await projectDetailSaveRef.current?.save();
            // After save completes, check if we have a pending project and switch to it
            if (pendingProjectIdRef.current !== null) {
              setSelectedProjectId(pendingProjectIdRef.current);
              pendingProjectIdRef.current = null;
            }
          } catch (err) {
            console.error('Failed to save before switching:', err);
            pendingProjectIdRef.current = null;
          }
        },
      });
      return;
    }
    setSelectedProjectId(projectId);
  };

  const handleCreateProject = async (data: CreateProjectRequest) => {
    try {
      // Backend now automatically creates budget request if budget is set
      await createProjectMutation.mutateAsync(data);
      success('Project was successfully created.');
      setShowNewProjectForm(false);
    } catch (err) {
      error('Failed to create project. Please try again.');
      console.error('Failed to create project:', err);
    }
  };

  const handleUpdateProject = async (data: UpdateProjectRequest) => {
    if (!selectedProjectId) return;
    try {
      // Backend now handles budget request creation/updates automatically
      await updateProjectMutation.mutateAsync(data);
      
      setHasUnsavedChanges(false);
      success('Project was successfully updated.');
    } catch (err) {
      error('Failed to update project. Please try again.');
      console.error('Failed to update project:', err);
    }
  };

  const handleDeleteProject = async () => {
    if (!selectedProjectId) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Project?',
      message: 'Are you sure you want to delete this project? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: async () => {
        try {
          const deletedId = selectedProjectId;
          await deleteProjectMutation.mutateAsync();
          // Select the first remaining project instead of showing blank state
          const remaining = projects.filter((p: ProjectDto) => p.projectId !== deletedId);
          setSelectedProjectId(remaining.length > 0 ? remaining[0].projectId : null);
          success('Project was successfully deleted.');
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          error('Failed to delete project. Please try again.');
          console.error('Failed to delete project:', err);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleCreateTask = async (data: CreateProjectTaskRequest) => {
    try {
      await createTaskMutation.mutateAsync(data);
      setShowNewTaskForm(false);
      success('Task was successfully created.');
    } catch (err) {
      error('Failed to create task. Please try again.');
      console.error('Failed to create task:', err);
    }
  };

  const handleEditTask = (task: ProjectTaskDto) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleUpdateTask = async (data: UpdateProjectTaskRequest) => {
    try {
      await updateTaskMutation.mutateAsync({
        taskId: editingTask?.taskId || 0,
        request: data,
      });
      success('Task was successfully updated.');
      setEditingTask(undefined);
      setShowTaskModal(false);
    } catch (err) {
      error('Failed to update task. Please try again.');
      console.error('Failed to update task:', err);
    }
  };

  const handleDeleteTask = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Task?',
      message: 'Are you sure you want to delete this task? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: async () => {
        try {
          await deleteTaskMutation.mutateAsync(editingTask?.taskId || 0);
          success('Task was successfully deleted.');
          setEditingTask(undefined);
          setShowTaskModal(false);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          error('Failed to delete task. Please try again.');
          console.error('Failed to delete task:', err);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleAddMessage = async (messageContent: string, parentMessageId?: number, taggedUserIds?: number[]) => {
    try {
      await createMessageMutation.mutateAsync({
        messageContent,
        parentMessageId,
        taggedUserIds,
      });
      success('Message was successfully posted.');
    } catch (err) {
      error('Failed to post message. Please try again.');
      console.error('Failed to post message:', err);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!canManageProjects) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column' }}>
        <AlertCircle style={{ width: '48px', height: '48px', color: '#ef4444', marginBottom: '16px' }} />
        <p style={{ fontSize: '16px', color: '#6b7280' }}>You do not have permission to access Project Tracking.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f9fafb', width: '100%', maxWidth: 'none' }}>
      <Toast toasts={toasts} onDismiss={dismissToast} />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        isDangerous={confirmDialog.isDangerous}
        onConfirm={() => {
          confirmDialog.onConfirm();
        }}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
      />
      <TaskModal
        isOpen={showTaskModal}
        task={editingTask}
        onClose={() => {
          setShowTaskModal(false);
          setEditingTask(undefined);
        }}
        onSave={editingTask ? handleUpdateTask : handleCreateTask}
        onDelete={editingTask && isUserAdmin ? handleDeleteTask : undefined}
        isAdmin={isUserAdmin}
        brgId={selectedProject?.brgId}
      />
      
      {/* Header with Filters */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '16px 80px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <FilterSelect
              label="BRG:"
              value={filters.brgId || ''}
              onChange={(e) => handleFilterChange('brgId', e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">All BRGs</option>
              {availableBRGs.map((brg: any) => (
                <option key={brg.brgId} value={brg.brgId}>{brg.name}</option>
              ))}
            </FilterSelect>

            <FilterSelect
              label="Company:"
              value={filters.company || ''}
              onChange={(e) => handleFilterChange('company', e.target.value || undefined)}
            >
              <option value="">All Companies</option>
              {companyOptions.map((company) => (
                <option key={company} value={company}>{company}</option>
              ))}
            </FilterSelect>

            <FilterSelect
              label="Status:"
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
            >
              <option value="">All Statuses</option>
              <option value="Planning Stage">Planning Stage</option>
              <option value="Requested">Requested</option>
              <option value="Approved">Approved</option>
              <option value="Budgeted">Budgeted</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </FilterSelect>

            <FilterSelect
              label="Priority:"
              value={filters.priority || ''}
              onChange={(e) => handleFilterChange('priority', e.target.value || undefined)}
            >
              <option value="">All Priorities</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </FilterSelect>

            {Object.values(filters).some(v => v !== undefined) && (
              <button 
                onClick={handleClearFilters}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear filters
              </button>
            )}
          </div>

          <button 
            onClick={() => setShowNewProjectForm(true)}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            + New Project
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', flex: 1, padding: '0 80px' }}>
        {/* Left Panel - Project List */}
        <div style={{ width: '300px', backgroundColor: 'white', borderRight: '1px solid #e5e7eb', overflowY: 'auto', paddingTop: '16px' }}>
          <h2 style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 16px 8px', margin: 0 }}>Projects</h2>
          {projects.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <AlertCircle style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 12px' }} />
              <p style={{ color: '#4b5563', marginBottom: '16px', fontSize: '14px' }}>No projects found for the selected filters.</p>
              <button 
                onClick={handleClearFilters}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div style={{ padding: '0 16px 16px' }}>
              {projects.map((project: ProjectDto) => {
                const statusColors: Record<string, { bg: string; text: string }> = {
                  'Planning Stage': { bg: '#dbeafe', text: '#1e40af' },
                  'Requested': { bg: '#fef3c7', text: '#92400e' },
                  'Approved': { bg: '#d1fae5', text: '#065f46' },
                  'Budgeted': { bg: '#ede9fe', text: '#5b21b6' },
                  'In Progress': { bg: '#fef3c7', text: '#92400e' },
                  'Completed': { bg: '#d1fae5', text: '#065f46' },
                };
                const statusStyle = statusColors[project.statusName || ''] || { bg: '#f3f4f6', text: '#374151' };
                return (
                  <div
                    key={project.projectId}
                    onClick={() => handleSelectProject(project.projectId)}
                    style={{
                      padding: '12px 16px',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      borderLeft: selectedProjectId === project.projectId ? '4px solid #2563eb' : '4px solid transparent',
                      backgroundColor: selectedProjectId === project.projectId ? '#eff6ff' : 'transparent',
                    }}
                  >
                    <h3 style={{ fontWeight: '600', fontSize: '14px', color: '#2563eb', margin: '0 0 4px 0' }}>{project.projectName}</h3>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 6px 0' }}>
                      Start Date: {project.startDate ? formatDateLocal(project.startDate) : 'TBD'} · {project.location || 'TBD'}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#374151' }}>{project.brgName}</span>
                      <span style={{ display: 'inline-block', backgroundColor: statusStyle.bg, color: statusStyle.text, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                        {project.statusName || 'Unknown'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Panel - Project Details */}
        <div style={{ flex: 1, minWidth: 0, backgroundColor: 'white', padding: '16px 24px' }}>
          {selectedProject ? (
            <ProjectDetailPanel
              ref={projectDetailSaveRef}
              project={selectedProject}
              onUpdate={handleUpdateProject}
              onDelete={handleDeleteProject}
              onAddTask={() => setShowNewTaskForm(true)}
              onEditTask={handleEditTask}
              onAddMessage={handleAddMessage}
              onUnsavedChanges={setHasUnsavedChanges}
              isAdmin={isUserAdmin}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <p style={{ color: '#6b7280' }}>Select a project to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* New Project Modal */}
      {showNewProjectForm && (
        <ProjectFormModal
          brgs={availableBRGs}
          onSubmit={handleCreateProject}
          onClose={() => setShowNewProjectForm(false)}
          isLoading={createProjectMutation.isPending}
        />
      )}

      {/* New Task Modal */}
      {showNewTaskForm && selectedProject && (
        <TaskFormModal
          onSubmit={handleCreateTask}
          onClose={() => setShowNewTaskForm(false)}
          isLoading={createTaskMutation.isPending}
          brgId={selectedProject.brgId}
        />
      )}
    </div>
  );
};

// Helper component - Project Detail Panel
interface ProjectDetailPanelProps {
  project: ProjectDto;
  onUpdate: (data: UpdateProjectRequest) => Promise<void>;
  onDelete: () => Promise<void>;
  onAddTask: () => void;
  onEditTask?: (task: ProjectTaskDto) => void;
  onUnsavedChanges: (hasChanges: boolean) => void;
  isAdmin: boolean;
  onAddMessage?: (message: string, parentMessageId?: number, taggedUserIds?: number[]) => Promise<void>;
}

export const ProjectDetailPanel = React.forwardRef<{ save: () => Promise<void> }, ProjectDetailPanelProps>(
  ({ project, onUpdate, onDelete, onAddTask, onEditTask, onUnsavedChanges, onAddMessage }, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [taggedUsers, setTaggedUsers] = useState<UserDropdownDto[]>([]);
  const [isPostingMessage, setIsPostingMessage] = useState(false);
  const [replyingToMessageId, setReplyingToMessageId] = useState<number | null>(null);
  const [replyingToName, setReplyingToName] = useState<string>('');
  const [dateValidationError, setDateValidationError] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const { data: pillars = [] } = useProjectPillars();
  const [formData, setFormData] = useState<UpdateProjectRequest>({
    projectName: project.projectName,
    location: project.location || '',
    startDate: project.startDate,
    endDate: project.endDate || '',
    budget: project.budget,
    statusId: project.statusId,
    projectDescription: project.projectDescription || '',
    goalsAlignment: project.goalsAlignment || '',
    notes: project.notes || '',
    pillarIds: project.pillarIds || [],
  });

  // Reset form data when the selected project changes
  React.useEffect(() => {
    setFormData({
      projectName: project.projectName,
      location: project.location || '',
      startDate: project.startDate,
      endDate: project.endDate || '',
      budget: project.budget,
      statusId: project.statusId,
      projectDescription: project.projectDescription || '',
      goalsAlignment: project.goalsAlignment || '',
      notes: project.notes || '',
      pillarIds: project.pillarIds || [],
    });
    setIsEditing(false);
    setNameError('');
  }, [project.projectId]);

  const handleChange = (field: keyof UpdateProjectRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setDateValidationError(''); // Clear error when user modifies dates
    onUnsavedChanges(true);
  };

  const handleCancel = () => {
    setFormData({
      projectName: project.projectName,
      location: project.location || '',
      startDate: project.startDate,
      endDate: project.endDate || '',
      budget: project.budget,
      statusId: project.statusId,
      projectDescription: project.projectDescription || '',
      goalsAlignment: project.goalsAlignment || '',
      notes: project.notes || '',
      pillarIds: project.pillarIds || [],
    });
    setIsEditing(false);
    setDateValidationError('');
    setNameError('');
    onUnsavedChanges(false);
  };

  // Helper function to validate that end date is not earlier than start date
  const validateDates = (): boolean => {
    if (!formData.startDate) {
      setDateValidationError('Start Date is required');
      return false;
    }

    // If end date is provided, validate it's not earlier than start date
    if (formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);

      if (endDate < startDate) {
        setDateValidationError('End Date cannot be earlier than Start Date');
        return false;
      }
    }

    setDateValidationError('');
    return true;
  };

  const handleSave = async () => {
    // Validate Program Name and dates before saving
    const nameValid = !!formData.projectName?.trim();
    setNameError(nameValid ? '' : PROGRAM_NAME_REQUIRED_MESSAGE);
    const datesValid = validateDates();
    if (!nameValid || !datesValid) {
      return;
    }

    try {
      await onUpdate({ ...formData, projectName: formData.projectName.trim() });
      setIsEditing(false);
      onUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save:', error);
    }
  };

  // Expose save function to parent via ref
  useImperativeHandle(ref, () => ({
    save: handleSave,
  }), [handleSave, formData, onUpdate, onUnsavedChanges]);

  const handlePostMessage = async () => {
    if (!messageInput.trim()) return;

    try {
      setIsPostingMessage(true);
      if (onAddMessage) {
        // Only users whose "@Name" is still in the text are tagged
        const taggedUserIds = taggedUsersInText(messageInput, taggedUsers).map((u) => u.userId);
        await onAddMessage(messageInput, replyingToMessageId ?? undefined, taggedUserIds);
        setMessageInput('');
        setTaggedUsers([]);
        setReplyingToMessageId(null);
        setReplyingToName('');
      }
    } catch (error) {
      console.error('Failed to post message:', error);
    } finally {
      setIsPostingMessage(false);
    }
  };

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2563eb', margin: 0, textDecoration: 'underline' }}>{project.projectName}</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '24px' }}>
        <div>
          <label htmlFor="edit-project-name" style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Program Name *</label>
          <input
            id="edit-project-name"
            type="text"
            value={formData.projectName}
            onChange={(e) => {
              handleChange('projectName', e.target.value);
              if (e.target.value.trim()) setNameError('');
            }}
            disabled={!isEditing}
            required
            aria-invalid={!!nameError}
            aria-describedby={nameError ? 'edit-project-name-error' : undefined}
            style={{ width: '100%', padding: '10px 12px', border: nameError ? '2px solid #dc2626' : '1px solid #d1d5db', borderRadius: '4px', backgroundColor: isEditing ? 'white' : '#f9fafb', fontSize: '14px' }}
          />
          {nameError && (
            <p id="edit-project-name-error" role="alert" style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>{nameError}</p>
          )}
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>BRG</label>
          <input
            type="text"
            value={project.brgName || ''}
            disabled
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: '#f9fafb', fontSize: '14px' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            disabled={!isEditing}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: isEditing ? 'white' : '#f9fafb', fontSize: '14px' }}
            placeholder="Online / In-person"
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pillar(s)</label>
          {isEditing ? (
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {pillars.map((pillar) => (
                <label key={pillar.pillarId} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={formData.pillarIds?.includes(pillar.pillarId) || false}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleChange('pillarIds', [...(formData.pillarIds || []), pillar.pillarId]);
                      } else {
                        handleChange('pillarIds', (formData.pillarIds || []).filter((id) => id !== pillar.pillarId));
                      }
                    }}
                    disabled={!isEditing}
                    style={{ cursor: isEditing ? 'pointer' : 'default' }}
                  />
                  <span>{pillar.pillarName}</span>
                </label>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {formData.pillarIds && formData.pillarIds.length > 0 ? (
                formData.pillarIds.map((pillarId) => {
                  const pillar = pillars.find((p) => p.pillarId === pillarId);
                  return pillar ? (
                    <span
                      key={pillarId}
                      style={{
                        display: 'inline-block',
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      {pillar.pillarName}
                    </span>
                  ) : null;
                })
              ) : (
                <span style={{ color: '#9ca3af', fontSize: '14px' }}>No pillars selected</span>
              )}
            </div>
          )}
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start Date *</label>
          <DateInputDDMMYYYY
            value={formData.startDate}
            onChange={(val) => handleChange('startDate', val)}
            disabled={!isEditing}
            style={{ width: '100%', padding: '10px 12px', border: dateValidationError && formData.startDate ? '2px solid #dc2626' : '1px solid #d1d5db', borderRadius: '4px', backgroundColor: isEditing ? 'white' : '#f9fafb', fontSize: '14px' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>End Date</label>
          <DateInputDDMMYYYY
            value={formData.endDate || ''}
            onChange={(val) => handleChange('endDate', val)}
            disabled={!isEditing}
            style={{ width: '100%', padding: '10px 12px', border: dateValidationError && formData.endDate ? '2px solid #dc2626' : '1px solid #d1d5db', borderRadius: '4px', backgroundColor: isEditing ? 'white' : '#f9fafb', fontSize: '14px' }}
          />
        </div>

        {dateValidationError && (
          <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', backgroundColor: '#fee2e2', borderRadius: '4px', color: '#dc2626', fontSize: '14px' }}>
            <AlertCircle style={{ width: '20px', height: '20px', flexShrink: 0 }} />
            <span>{dateValidationError}</span>
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Budget</label>
          <input
            type="number"
            value={formData.budget ?? ''}
            onChange={(e) => handleChange('budget', e.target.value ? Number(e.target.value) : undefined)}
            disabled={!!project.budget || !isEditing}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: (!!project.budget || !isEditing) ? '#f9fafb' : 'white', fontSize: '14px' }}
            placeholder="$0"
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</label>
          <select
            value={formData.statusId || ''}
            onChange={(e) => handleChange('statusId', e.target.value ? Number(e.target.value) : undefined)}
            disabled={!isEditing}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: isEditing ? 'white' : '#f9fafb', fontSize: '14px' }}
          >
            <option value="">Select Status</option>
            <option value="1">Planning Stage</option>
            <option value="2">Requested</option>
            <option value="3">Approved</option>
            <option value="4">Budgeted</option>
            <option value="5">In Progress</option>
            <option value="6">Completed</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Program Description</label>
          <textarea
            value={formData.projectDescription}
            onChange={(e) => handleChange('projectDescription', e.target.value)}
            disabled={!isEditing}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: isEditing ? 'white' : '#f9fafb', fontFamily: 'inherit', fontSize: '14px', resize: 'vertical' }}
            rows={4}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>How does this event relate to your BRG goals?</label>
          <textarea
            value={formData.goalsAlignment}
            onChange={(e) => handleChange('goalsAlignment', e.target.value)}
            disabled={!isEditing}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: isEditing ? 'white' : '#f9fafb', fontFamily: 'inherit', fontSize: '14px', resize: 'vertical' }}
            rows={3}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            disabled={!isEditing}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: isEditing ? 'white' : '#f9fafb', fontFamily: 'inherit', fontSize: '14px', resize: 'vertical' }}
            rows={3}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
        <button 
          onClick={onDelete}
          style={{
            backgroundColor: 'white',
            color: '#dc2626',
            padding: '10px 20px',
            borderRadius: '4px',
            border: '2px solid #dc2626',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Delete Project
        </button>

        {isEditing ? (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              onClick={handleCancel}
              style={{
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              disabled={!!dateValidationError}
              style={{
                backgroundColor: dateValidationError ? '#d1d5db' : '#2563eb',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                border: 'none',
                cursor: dateValidationError ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                opacity: dateValidationError ? 0.6 : 1
              }}
            >
              Save Changes
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              onClick={() => setIsEditing(true)}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {/* Tasks Section */}
      <div style={{ marginTop: '32px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Tasks</h3>
          <button
            onClick={onAddTask}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              color: '#1a73e8'
            }}
          >
            + [Create New Task]
          </button>
        </div>

        <TasksGrid
          tasks={project?.tasks || []}
          onEditTask={onEditTask}
        />
      </div>

      {/* Activity & Messages Section */}
      <div style={{ marginTop: '32px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0, marginBottom: '20px' }}>Activity & Messages</h3>
        
        {/* Messages Display */}
        <div style={{ marginBottom: '20px', maxHeight: '300px', overflowY: 'auto' }}>
          {project.messages && project.messages.length > 0 ? (
            <div>
              {project.messages.map((message, index) => (
                <div key={message.messageId} style={{ paddingBottom: '16px', marginBottom: '16px', borderBottom: index < project.messages.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                    <p style={{ fontWeight: '600', color: '#2563eb', margin: 0, fontSize: '14px' }}>{message.createdByName}</p>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                      {new Date(message.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(message.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                    </p>
                  </div>
                  <p style={{ color: '#374151', fontSize: '14px', margin: '4px 0 0 0', lineHeight: '1.5', wordBreak: 'break-word' }}>
                    {renderMessageWithTags(message.messageContent, message.taggedUsers)}
                  </p>
                  <button
                    onClick={() => {
                      setReplyingToMessageId(message.messageId);
                      setReplyingToName(message.createdByName);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      padding: 0,
                      marginTop: '8px',
                    }}
                  >
                    ↩ Reply
                  </button>
                  {message.replies && message.replies.length > 0 && (
                    <div style={{ marginTop: '12px', marginLeft: '16px', paddingLeft: '12px', borderLeft: '3px solid #e5e7eb' }}>
                      {message.replies.map((reply) => (
                        <div key={reply.messageId} style={{ marginBottom: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                            <p style={{ fontWeight: '600', color: '#374151', margin: 0, fontSize: '13px' }}>{reply.createdByName}</p>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>
                              {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(reply.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </p>
                          </div>
                          <p style={{ color: '#6b7280', fontSize: '13px', margin: '2px 0 0 0', lineHeight: '1.4', wordBreak: 'break-word' }}>
                            {renderMessageWithTags(reply.messageContent, reply.taggedUsers)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', margin: 0 }}>No messages yet. Add a message to start collaborating on this project.</p>
          )}
        </div>

        {/* Message Input */}
        <div>
          {replyingToMessageId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '6px 10px', backgroundColor: '#eff6ff', borderRadius: '4px', border: '1px solid #bfdbfe', fontSize: '12px', color: '#1e40af' }}>
              <span>Replying to <strong>{replyingToName}</strong></span>
              <button
                onClick={() => { setReplyingToMessageId(null); setReplyingToName(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '14px', padding: '0 4px' }}
              >
                &times;
              </button>
            </div>
          )}
          {/* No overflow:hidden here so the @tag list can show above the input */}
          <div style={{ display: 'flex', gap: '0', border: '1px solid #d1d5db', borderRadius: '6px' }}>
            <MentionInput
              value={messageInput}
              onChange={setMessageInput}
              taggedUsers={taggedUsers}
              onTaggedUsersChange={setTaggedUsers}
              brgId={project.brgId}
              onSubmit={handlePostMessage}
              placeholder="Write a message or update... Type @ to tag someone"
              disabled={isPostingMessage}
              style={{
                flex: 1,
                padding: '10px 14px',
                border: 'none',
                borderRadius: '6px 0 0 6px',
                outline: 'none',
                fontSize: '14px',
                opacity: isPostingMessage ? 0.6 : 1
              }}
            />
            <button
              onClick={handlePostMessage}
              disabled={isPostingMessage || !messageInput.trim()}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '0 5px 5px 0',
                cursor: isPostingMessage || !messageInput.trim() ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isPostingMessage || !messageInput.trim() ? 0.6 : 1
              }}
            >
              {isPostingMessage ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
ProjectDetailPanel.displayName = 'ProjectDetailPanel';

export const BUDGET_REQUIRED_MESSAGE = 'Budget is required. Enter $0 if there is no associated spend.';
export const PROGRAM_NAME_REQUIRED_MESSAGE = 'Program Name is required.';

// Helper component - New Project Form Modal
export const ProjectFormModal: React.FC<{
  brgs: any[];
  onSubmit: (data: CreateProjectRequest) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
}> = ({ brgs, onSubmit, onClose, isLoading }) => {
  const [formData, setFormData] = useState({
    brgId: 0,
    projectName: '',
    location: '',
    startDate: '',
    endDate: undefined as string | undefined,
    budget: undefined as number | undefined,
    statusId: 1, // Default to Planning Stage
    projectDescription: '',
    goalsAlignment: '',
    notes: '',
    pillarIds: [] as number[],
  });
  const [timeValidationError, setTimeValidationError] = useState<string>('');
  // Budget is kept as text so only digits and one decimal point (max 2 places) can be typed
  const [budgetInput, setBudgetInput] = useState<string>('');
  const [budgetError, setBudgetError] = useState<string>('');
  const [brgError, setBrgError] = useState<string>('');
  const [projectNameError, setProjectNameError] = useState<string>('');
  const { data: pillars = [] } = useProjectPillars();

  const validateBrg = (): boolean => {
    if (!formData.brgId) {
      setBrgError('BRG is required.');
      return false;
    }
    setBrgError('');
    return true;
  };

  const validateBudget = (): boolean => {
    if (budgetInput.trim() === '') {
      setBudgetError(BUDGET_REQUIRED_MESSAGE);
      return false;
    }
    if (Number.isNaN(Number(budgetInput))) {
      setBudgetError('Enter a valid dollar amount.');
      return false;
    }
    setBudgetError('');
    return true;
  };

  // Helper function to validate that end date is not earlier than start date
  const validateDates = (): boolean => {
    if (!formData.startDate) {
      setTimeValidationError('Start Date is required');
      return false;
    }

    // If end date is provided, validate it's not earlier than start date
    if (formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);

      if (endDate < startDate) {
        setTimeValidationError('End Date cannot be earlier than Start Date');
        return false;
      }
    }

    setTimeValidationError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate Program Name, BRG, dates and budget before submitting
    const nameValid = !!formData.projectName.trim();
    setProjectNameError(nameValid ? '' : PROGRAM_NAME_REQUIRED_MESSAGE);
    const brgValid = validateBrg();
    const datesValid = validateDates();
    const budgetValid = validateBudget();
    if (!nameValid || !brgValid || !datesValid || !budgetValid) {
      return;
    }

    try {
      await onSubmit({
        ...formData,
        projectName: formData.projectName.trim(),
        budget: Number(budgetInput),
      } as CreateProjectRequest);
      onClose();
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ width: '100%', maxWidth: '640px', maxHeight: '85vh', overflowY: 'auto', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
        {/* Header */}
        <div style={{ backgroundColor: '#2563eb', color: 'white', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderRadius: '4px 4px 0 0' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'white', margin: 0, marginBottom: '4px' }}>New Project</h2>
            <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.9)', margin: 0 }}>Create a new BRG project or engagement</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ backgroundColor: 'transparent', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer', padding: '0', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '16px', marginBottom: '24px' }}>
            {/* Row 1: Program Name | BRG */}
            <div style={{ gridColumn: '1 / 4' }}>
              <label htmlFor="project-name" style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Program Name *</label>
              <input
                id="project-name"
                type="text"
                value={formData.projectName}
                onChange={(e) => {
                  const projectName = e.target.value;
                  setFormData((prev) => ({ ...prev, projectName }));
                  if (projectName.trim()) setProjectNameError('');
                }}
                placeholder="Enter program or event name"
                required
                aria-invalid={!!projectNameError}
                aria-describedby={projectNameError ? 'project-name-error' : undefined}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${projectNameError ? '#dc2626' : '#d1d5db'}`, borderRadius: '4px', fontSize: '14px' }}
              />
              {projectNameError && (
                <p id="project-name-error" role="alert" style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>{projectNameError}</p>
              )}
            </div>

            <div style={{ gridColumn: '4 / 7' }}>
              <label htmlFor="project-brg" style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>BRG *</label>
              <select
                id="project-brg"
                data-testid="project-brg-select"
                value={formData.brgId || ''}
                onChange={(e) => {
                  const brgId = Number(e.target.value) || 0;
                  setFormData((prev) => ({ ...prev, brgId }));
                  if (brgId) setBrgError('');
                }}
                required
                aria-invalid={!!brgError}
                aria-describedby={brgError ? 'project-brg-error' : undefined}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${brgError ? '#dc2626' : '#d1d5db'}`, borderRadius: '4px', fontSize: '14px' }}
              >
                {/* Empty value so the browser's required check blocks submit until a BRG is chosen */}
                <option value="">Select BRG</option>
                {brgs.map((brg: any) => (
                  <option key={brg.brgId} value={brg.brgId}>{brg.name}</option>
                ))}
              </select>
              {brgError && (
                <p id="project-brg-error" role="alert" style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>{brgError}</p>
              )}
            </div>

            {/* Row 2: Location | Pillar(s) */}
            <div style={{ gridColumn: '1 / 4' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. Online / Microsoft Teams, In Person"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
              />
            </div>

            <div style={{ gridColumn: '4 / 7' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Pillar(s) - Select all that apply</label>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {pillars.map((pillar) => (
                  <label key={pillar.pillarId} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={formData.pillarIds.includes(pillar.pillarId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            pillarIds: [...prev.pillarIds, pillar.pillarId],
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            pillarIds: prev.pillarIds.filter((id) => id !== pillar.pillarId),
                          }));
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>{pillar.pillarName}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Row 3: Start Date | End Date */}
            <div style={{ gridColumn: '1 / 4' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Start Date *</label>
              <DateInputDDMMYYYY
                value={formData.startDate}
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, startDate: val }));
                  setTimeValidationError('');
                }}
                required
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
              />
            </div>

            <div style={{ gridColumn: '4 / 7' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>End Date</label>
              <DateInputDDMMYYYY
                value={formData.endDate || ''}
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, endDate: val || undefined }));
                  setTimeValidationError('');
                }}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
              />
            </div>

            {timeValidationError && (
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', backgroundColor: '#fee2e2', borderRadius: '4px', color: '#dc2626', fontSize: '14px' }}>
                <AlertCircle style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                <span>{timeValidationError}</span>
              </div>
            )}

            {/* Row 4: Budget | Status */}
            <div style={{ gridColumn: '1 / 4' }}>
              <label htmlFor="project-budget" style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Budget *</label>
              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                <span style={{ padding: '8px 10px', border: `1px solid ${budgetError ? '#dc2626' : '#d1d5db'}`, borderRight: 'none', borderRadius: '4px 0 0 4px', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '14px' }}>$</span>
                <input
                  id="project-budget"
                  type="text"
                  inputMode="decimal"
                  data-testid="project-budget-input"
                  value={budgetInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Accept only non-negative dollar amounts: digits with an optional 2-place decimal
                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                      setBudgetInput(value);
                      if (budgetError && value.trim() !== '') setBudgetError('');
                    }
                  }}
                  placeholder="0.00"
                  aria-required="true"
                  aria-invalid={!!budgetError}
                  aria-describedby={budgetError ? 'project-budget-error' : 'project-budget-help'}
                  style={{ width: '100%', padding: '8px 12px', border: `1px solid ${budgetError ? '#dc2626' : '#d1d5db'}`, borderRadius: '0 4px 4px 0', fontSize: '14px' }}
                />
              </div>
              {budgetError ? (
                <p id="project-budget-error" role="alert" style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>{budgetError}</p>
              ) : (
                <p id="project-budget-help" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>A budget greater than $0 automatically creates a Pending Budget Request in the Budget Tracking dashboard</p>
              )}
            </div>

            <div style={{ gridColumn: '4 / 7' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Status</label>
              <select
                value={formData.statusId}
                onChange={(e) => setFormData((prev) => ({ ...prev, statusId: Number(e.target.value) }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
              >
                <option value="1">Planning Stage</option>
                <option value="2">Requested</option>
                <option value="3">Approved</option>
                <option value="4">Budgeted</option>
                <option value="5">In Progress</option>
                <option value="6">Completed</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Program Description</label>
            <textarea
              value={formData.projectDescription}
              onChange={(e) => setFormData((prev) => ({ ...prev, projectDescription: e.target.value }))}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px' }}
              rows={3}
              placeholder="Describe the program or event..."
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>How does this event relate to your BRG goals?</label>
            <textarea
              value={formData.goalsAlignment}
              onChange={(e) => setFormData((prev) => ({ ...prev, goalsAlignment: e.target.value }))}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px' }}
              rows={3}
              placeholder="Explain how this supports your BRG's OKRs or pillars..."
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px' }}
              rows={3}
              placeholder="Any additional notes or updates..."
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '24px', borderTop: '1px solid #e5e7eb', marginTop: '24px' }}>
            <button 
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                color: '#374151',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={isLoading || !!timeValidationError}
              style={{
                backgroundColor: isLoading || timeValidationError ? '#d1d5db' : '#2563eb',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                border: 'none',
                cursor: isLoading || timeValidationError ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading || timeValidationError ? 0.6 : 1
              }}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// TasksGrid Component using AG Grid
interface TasksGridProps {
  tasks: ProjectTaskDto[];
  onEditTask?: (task: ProjectTaskDto) => void;
}

export const TasksGrid: React.FC<TasksGridProps> = ({ tasks, onEditTask }) => {
  const gridRef = useRef<AgGridReact>(null);

  // Custom CSS for AG Grid styling (matching other grids)
  const customGridStyles = `
    .ag-theme-quartz {
      --ag-header-background-color: #1a3bcc;
      --ag-header-foreground-color: #fff;
      --ag-header-cell-text-color: #fff;
      --ag-header-cell-padding: 8px 10px;
      --ag-header-cell-hover-background-color: #1530a8;
      --ag-header-cell-moving-background-color: #1530a8;
      --ag-font-size: 11px;
      --ag-cell-horizontal-padding: 10px;
      --ag-cell-vertical-padding: 8px;
      --ag-row-height: 32px;
      --ag-row-border-color: #f0f0f0;
      background-color: #ffffff;
      border: none;
      border-radius: 0;
    }
    
    .ag-theme-quartz .ag-header {
      font-weight: 700;
      font-size: 11px;
      height: 32px;
      background-color: #1a3bcc;
    }
    
    .ag-theme-quartz .ag-header-cell {
      background-color: #1a3bcc !important;
      color: #fff !important;
      border-right: 1px solid #1530a8;
      font-weight: 700;
    }
    
    .ag-theme-quartz .ag-header-cell:last-child {
      border-right: none;
    }
    
    .ag-theme-quartz .ag-row {
      border-bottom: 1px solid #f0f0f0;
      height: 32px;
      background-color: #ffffff;
    }
    
    .ag-theme-quartz .ag-row:last-child {
      border-bottom: none;
    }
    
    .ag-theme-quartz .ag-row:hover {
      background-color: #f7f9ff !important;
    }
    
    .ag-theme-quartz .ag-cell {
      font-size: 11px;
      color: #333;
      padding: 8px 10px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
    }
    
    .ag-theme-quartz .ag-root {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    }
    
    .ag-theme-quartz .ag-root-wrapper {
      border: none;
    }
    
    .ag-theme-quartz .ag-center-cols-viewport {
      overflow-x: auto;
    }
  `;

  interface TaskRow {
    taskId: number;
    taskName: string;
    assigneeNames: string;
    startDate: string;
    dueDate: string;
    status: string;
    priority: string;
  }

  const getStatusBadgeStyle = (status: string) => {
    const statusMap: Record<string, { bg: string; text: string }> = {
      'Completed': { bg: '#dcfce7', text: '#166534' },
      'In Progress': { bg: '#dbeafe', text: '#1e40af' },
      'default': { bg: '#f3e8ff', text: '#6b21a8' }
    };
    return statusMap[status] || statusMap['default'];
  };

  const getPriorityBadgeStyle = (priority: string) => {
    const priorityMap: Record<string, { bg: string; text: string }> = {
      'High': { bg: '#fee2e2', text: '#991b1b' },
      'Medium': { bg: '#fef3c7', text: '#92400e' },
      'Low': { bg: '#dbeafe', text: '#1e40af' }
    };
    return priorityMap[priority] || priorityMap['Low'];
  };

  const columnDefs: ColDef<TaskRow>[] = useMemo(
    () => [
      {
        field: 'taskName',
        headerName: 'Task',
        flex: 1.2,
        minWidth: 150,
      },
      {
        field: 'assigneeNames',
        headerName: 'Assigned To',
        flex: 1.2,
        minWidth: 140,
        // Single line: names that don't fit are cut off with "…"; hover shows every assignee
        cellRenderer: (params: any) => (
          <span
            data-testid="task-assignees"
            title={params.value || undefined}
            style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {params.value || '—'}
          </span>
        ),
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        flex: 0.9,
        minWidth: 100,
        valueFormatter: (params) => params.value ? formatDateLocal(params.value) : '—',
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        flex: 0.9,
        minWidth: 100,
        valueFormatter: (params) => params.value ? formatDateLocal(params.value) : '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 90,
        cellRenderer: (params: any) => {
          const style = getStatusBadgeStyle(params.value);
          return (
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '500',
              backgroundColor: style.bg,
              color: style.text,
              whiteSpace: 'nowrap'
            }}>
              {params.value}
            </span>
          );
        },
      },
      {
        field: 'priority',
        headerName: 'Priority',
        flex: 0.8,
        minWidth: 85,
        cellRenderer: (params: any) => {
          const style = getPriorityBadgeStyle(params.value);
          return (
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '500',
              backgroundColor: style.bg,
              color: style.text,
              whiteSpace: 'nowrap'
            }}>
              {params.value}
            </span>
          );
        },
      },
      {
        field: 'taskId',
        headerName: '',
        flex: 0.5,
        minWidth: 70,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => (
          <button
            onClick={() => {
              const task = tasks.find((t) => t.taskId === params.value);
              if (task) onEditTask?.(task);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#2563eb',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              textDecoration: 'underline',
              padding: 0,
              whiteSpace: 'nowrap'
            }}
          >
            details
          </button>
        ),
      },
    ],
    [tasks, onEditTask]
  );

  const gridOptions: GridOptions<TaskRow> = {
    domLayout: 'normal',
    rowHeight: 32,
    headerHeight: 32,
    suppressColumnVirtualisation: false,
    suppressCellFocus: true,
    suppressMovableColumns: true,
  };

  const rowData: TaskRow[] = useMemo(
    () =>
      tasks.map((task) => ({
        taskId: task.taskId,
        taskName: task.taskName,
        assigneeNames: (task.assignees ?? []).map((a) => a.displayName).join(', '),
        startDate: task.startDate || '',
        dueDate: task.dueDate || '',
        status: task.status || 'Not Started',
        priority: task.priority || 'Medium',
      })),
    [tasks]
  );

  if (tasks.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '6px', color: '#6b7280', fontSize: '14px' }}>
        No tasks created yet
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '6px',
        overflow: 'hidden',
        height: '300px',
      }}
    >
      <style>{customGridStyles}</style>
      <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
        <AgGridReact<TaskRow>
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          gridOptions={gridOptions}
          defaultColDef={{
            resizable: false,
            sortable: false,
            filter: false,
          }}
        />
      </div>
    </div>
  );
};

// Task Form Modal Component
export const TaskFormModal: React.FC<{
  onSubmit: (data: CreateProjectTaskRequest) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
  /** Project's BRG — assignee type-ahead only offers its members. */
  brgId?: number;
}> = ({ onSubmit, onClose, isLoading, brgId }) => {
  const [formData, setFormData] = useState<CreateProjectTaskRequest>({
    taskName: '',
    status: 'Not Started',
    priority: 'Medium',
    startDate: '',
    dueDate: '',
    notes: '',
  });
  const [assignees, setAssignees] = useState<UserDropdownDto[]>([]);
  const [dateValidationError, setDateValidationError] = useState<string>('');

  // Helper function to validate that due date is after start date
  const validateDates = (): boolean => {
    if (!formData.startDate || !formData.dueDate) {
      return true; // Skip validation if either date is empty
    }

    // Parse dates in ISO format (yyyy-mm-dd)
    const parseDate = (dateStr: string): Date | null => {
      // Check if it's already in ISO format (yyyy-mm-dd)
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length !== 3) return null;
        
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        
        if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
        
        return new Date(year, month - 1, day);
      }
      
      return null;
    };

    const startDateObj = parseDate(formData.startDate);
    const dueDateObj = parseDate(formData.dueDate);

    if (!startDateObj || !dueDateObj) {
      return true; // Skip validation if dates are malformed
    }

    if (dueDateObj < startDateObj) {
      setDateValidationError('Due Date must be on or after Start Date.');
      return false;
    }

    setDateValidationError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate dates before submitting
    if (!validateDates()) {
      return;
    }

    try {
      await onSubmit({ ...formData, assigneeUserIds: assignees.map((u) => u.userId) });
      setFormData({
        taskName: '',
        status: 'Not Started',
        priority: 'Medium',
        startDate: '',
        dueDate: '',
        notes: '',
      });
      setAssignees([]);
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ width: '100%', maxWidth: '420px', maxHeight: '90vh', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ backgroundColor: '#2563eb', color: 'white', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: '600', color: 'white', margin: 0, marginBottom: '2px' }}>New Task</h2>
            <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.9)', margin: 0 }}>Create a new task for this project</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ backgroundColor: 'transparent', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', padding: '0', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '3px' }}>Task Name *</label>
            <input
              type="text"
              required
              value={formData.taskName}
              onChange={(e) => setFormData((prev) => ({ ...prev, taskName: e.target.value }))}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
              placeholder="Enter task name..."
            />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <TaskAssigneesField
              value={assignees}
              onChange={setAssignees}
              brgId={brgId}
              labelStyle={{ fontSize: '12px', fontWeight: 500, color: '#374151', marginBottom: '3px' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '3px' }}>Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData((prev) => ({ ...prev, priority: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
              >
                <option value="Not Started">Not Started</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '3px' }}>Start Date</label>
              <DateInputDDMMYYYY
                value={formData.startDate || ''}
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, startDate: val }));
                  setDateValidationError('');
                }}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '3px' }}>Due Date</label>
              <DateInputDDMMYYYY
                value={formData.dueDate || ''}
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, dueDate: val }));
                  setDateValidationError('');
                }}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {dateValidationError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', backgroundColor: '#fee2e2', borderRadius: '4px', color: '#dc2626', fontSize: '14px', marginBottom: '16px' }}>
              <AlertCircle style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <span>{dateValidationError}</span>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px', boxSizing: 'border-box' }}
              rows={3}
              placeholder="Add any notes or details..."
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '24px', borderTop: '1px solid #e5e7eb', marginTop: '24px' }}>
            <button 
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                color: '#374151',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={isLoading || !!dateValidationError}
              style={{
                backgroundColor: isLoading || dateValidationError ? '#d1d5db' : '#2563eb',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '4px',
                border: 'none',
                cursor: isLoading || dateValidationError ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading || dateValidationError ? 0.6 : 1,
              }}
            >
              {isLoading ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
